import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { BaseChartProps, shapeMap } from './BaseChart';
import { DataPoint } from '../../../types/chart';

const BarChart: React.FC<BaseChartProps> = ({
  data,
  seriesList,
  seriesInfo,
  seriesVisibility,
  xAxisLabel,
  yAxisLabel,
  xLogScale,
  yLogScale,
  xMinValue,
  xMaxValue,
  yMinValue,
  yMaxValue,
  onSeriesClick,
  onVisibilityToggle
}) => {
  const chartRef = useRef<HTMLDivElement>(null);

  // 计算系列的统计信息（和、最大值、最小值、平均值）
  const calculateSeriesStats = (seriesData: DataPoint[]) => {
    if (seriesData.length === 0) return { sum: 0, min: 0, max: 0, avg: 0 };
    
    const yValues = seriesData.map(d => d.y);
    const min = Math.min(...yValues);
    const max = Math.max(...yValues);
    const sum = yValues.reduce((a, b) => a + b, 0);
    const avg = sum / yValues.length;
    
    return {
      sum: parseFloat(sum.toFixed(2)),
      min: parseFloat(min.toFixed(2)),
      max: parseFloat(max.toFixed(2)),
      avg: parseFloat(avg.toFixed(2))
    };
  };

  useEffect(() => {
    if (!chartRef.current) return;
    
    // 清除之前的图表和tooltip
    d3.select(chartRef.current).selectAll("*").remove();
    
    const margin = { top: 20, right: 200, bottom: 100, left: 60 }; // 增加底部边距
    const width = 700 - margin.left - margin.right;
    const height = 400 - margin.top - margin.bottom;
    
    // 创建SVG元素
    const svg = d3.select(chartRef.current)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);
    
    // 检查是否有数据
    if (data.length === 0) {
      // 没有数据时显示提示文本
      svg.append("text")
        .attr("x", width / 2)
        .attr("y", height / 2)
        .attr("text-anchor", "middle")
        .style("font-size", "14px")
        .style("fill", "#666")
        .text("没有可显示的数据。");
      
      // 即使没有数据，仍然添加坐标轴和图例
      createAxesAndLegend(svg, width, height, margin);
      return;
    }
    
    // 过滤出可见的数据
    const visibleData = data.filter(d => d.series && (seriesVisibility[d.series] !== false));
    
    // 设置 x 轴比例尺 - 使用 band scale 处理分类数据
    const uniqueXValues: Array<string | number> = [];
    data.forEach(d => {
      if (!uniqueXValues.includes(d.x)) {
        uniqueXValues.push(d.x);
      }
    });
    
    const xScale = d3.scaleBand()
      .domain(uniqueXValues.map(x => String(x)))
      .range([0, width])
      .padding(0.2);
    
    // 设置 y 轴比例尺
    const yScale = yLogScale
      ? d3.scaleLog().domain([Math.max(0.001, yMinValue), yMaxValue])
      : d3.scaleLinear().domain([yMinValue, yMaxValue]);
    yScale.range([height, 0]);
    
    // 添加 X 轴
    const xAxis = d3.axisBottom(xScale);

    // 绘制x轴
    const xAxisGroup = svg.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(xAxis);

    // 旋转x轴刻度标签
    xAxisGroup.selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .attr("transform", "rotate(-45)");

    // 计算x轴标签的合适位置
    // 获取x轴高度 (包括刻度和旋转后的文本)
    const xAxisHeight = xAxisGroup.node()?.getBBox().height || 30;
    const xLabelDistance = xAxisHeight + 10; // 在x轴高度基础上增加10px的间距

    // 添加X轴标签
    // 计算标签宽度 - 根据内容估算
    const estimatedLabelWidth = xAxisLabel.length * 7; // 每个字符约7px
    const labelWidth = Math.max(150, Math.min(width, estimatedLabelWidth));

    const xLabelContainer = d3.select(chartRef.current)
      .select("svg")
      .append("g")
      .attr("transform", `translate(${margin.left + width/2}, ${margin.top + height + xLabelDistance})`);

    // 支持HTML标签（包括上下标）
    xLabelContainer.append("foreignObject")
      .attr("width", labelWidth)
      .attr("height", 40) // 增加高度以容纳可能的换行
      .attr("x", -labelWidth/2)  // 居中
      .attr("y", 0)
      .attr("class", "x-axis-label") // 添加类名便于识别
      .append("xhtml:div")
      .style("text-align", "center")
      .style("font-size", "12px")
      .style("overflow-wrap", "break-word") // 允许长文本换行
      .html(xAxisLabel);
    
    // 添加Y轴
    svg.append("g")
      .call(d3.axisLeft(yScale));
    
    // 添加Y轴标签
    const yLabelContainer = d3.select(chartRef.current)
      .select("svg")
      .append("g")
      .attr("transform", `translate(${margin.left - 40}, ${margin.top + height/2}) rotate(-90)`);
    
    // 支持HTML标签（包括上下标）
    yLabelContainer.append("foreignObject")
      .attr("width", 200)
      .attr("height", 30)
      .attr("x", -100)  // 居中
      .attr("y", -15)
      .append("xhtml:div")
      .style("text-align", "center")
      .style("font-size", "12px")
      .html(yAxisLabel);
    
    // 如果没有可见数据，显示提示文本，但仍然显示图例
    if (visibleData.length === 0) {
      svg.append("text")
        .attr("x", width / 2)
        .attr("y", height / 2)
        .attr("text-anchor", "middle")
        .style("font-size", "14px")
        .style("fill", "#666")
        .text("没有可见的数据。请在图例中选择要显示的系列。");
    }
    
    // 创建交互提示框
    const tooltip = d3.select(chartRef.current)
      .append("div")
      .attr("class", "chart-tooltip")
      .style("position", "absolute")
      .style("visibility", "hidden")
      .style("opacity", "0")
      .style("display", "none")
      .style("background", "white")
      .style("border", "1px solid #ddd")
      .style("border-radius", "4px")
      .style("padding", "8px")
      .style("box-shadow", "0 0 5px rgba(0,0,0,0.1)")
      .style("pointer-events", "none")
      .style("font-size", "12px")
      .style("z-index", "1000");
    
    // 辅助函数：获取系列的颜色
    const getSeriesColor = (series: string) => {
      return seriesInfo[series]?.color || "#000000";
    };
    
    // 添加图例
    const legend = d3.select(chartRef.current)
      .select("svg")
      .append("g")
      .attr("transform", `translate(${margin.left + width + 30}, ${margin.top})`);
    
    // 记录累计高度，以便垂直排列图例项
    let cumulativeHeight = 0;
    
    seriesList.forEach((series) => {
      const seriesData = data.filter(d => d.series === series);
      if (seriesData.length === 0) return;
      
      const color = getSeriesColor(series);
      const stats = calculateSeriesStats(seriesData);
      const itemHeight = 68; // 增加高度以容纳统计信息
      const isVisible = seriesVisibility[series] !== false;
      
      // 图例项容器
      const legendItem = legend.append("g")
        .attr("class", "legend-item")
        .attr("data-series", series)
        .attr("transform", `translate(0, ${cumulativeHeight})`)
        .style("cursor", "pointer")
        .on("click", () => {
          if (onSeriesClick) {
            onSeriesClick(series);
          }
        });
      
      // 添加可见性切换图标
      legendItem.append("text")
        .attr("class", "visibility-toggle-icon")
        .attr("x", -15)
        .attr("y", 14)
        .style("cursor", "pointer")
        .style("font-size", "14px")
        .text(isVisible ? '○' : '●')
        .on('click', function(event) {
          event.stopPropagation();
          if (onVisibilityToggle) {
            onVisibilityToggle(series);
          }
        });
      
      // 绘制图例标记 - 对于柱状图，使用小矩形
      legendItem.append("rect")
        .attr("x", 0)
        .attr("y", 5)
        .attr("width", 10)
        .attr("height", 10)
        .style("fill", color);
      
      // 图例文本 - 系列名称
      legendItem.append("text")
        .attr("x", 15)
        .attr("y", 14)
        .attr("text-anchor", "start")
        .style("font-weight", "bold")
        .style("font-size", "12px")
        .style("fill", "#333")
        .html(seriesInfo[series]?.name || series);
      
      // 添加统计信息
      legendItem.append("text")
        .attr("x", 15)
        .attr("y", 32)
        .attr("text-anchor", "start")
        .style("font-size", "10px")
        .style("fill", "#666")
        .html(`和: ${stats.sum}`);
      
      legendItem.append("text")
        .attr("x", 15)
        .attr("y", 44)
        .attr("text-anchor", "start")
        .style("font-size", "10px")
        .style("fill", "#666")
        .html(`最大值: ${stats.max}`);
      
      legendItem.append("text")
        .attr("x", 15)
        .attr("y", 56)
        .attr("text-anchor", "start")
        .style("font-size", "10px")
        .style("fill", "#666")
        .html(`最小值: ${stats.min}`);
      
      legendItem.append("text")
        .attr("x", 15)
        .attr("y", 68)
        .attr("text-anchor", "start")
        .style("font-size", "10px")
        .style("fill", "#666")
        .html(`平均值: ${stats.avg}`);
      
      // 更新累计高度
      cumulativeHeight += itemHeight + 10; // 增加间距
    });
    
    // 如果有可见数据，绘制柱状图
    if (visibleData.length > 0) {
      // 重新组织数据为分组柱状图格式
      const groupedData: Map<number | string, Map<string, number>> = new Map();
      
      // 收集每个分组下各个序列的值
      uniqueXValues.forEach(xValue => {
        const seriesMap = new Map<string, number>();
        seriesList.forEach(series => {
          if (seriesVisibility[series] !== false) {
            const point = visibleData.find(d => d.series === series && d.x === xValue);
            if (point) {
              seriesMap.set(series, point.y);
            }
          }
        });
        groupedData.set(xValue, seriesMap);
      });
      
      // 设置序列的内部 x 轴比例尺
      const xSubScale = d3.scaleBand()
        .domain(seriesList.filter(s => seriesVisibility[s] !== false))
        .range([0, xScale.bandwidth()])
        .padding(0.05);
      
      // 绘制分组柱状图
      uniqueXValues.forEach(xValue => {
        const seriesMap = groupedData.get(xValue);
        if (!seriesMap) return;
        
        // 获取可见的序列
        const visibleSeries = seriesList.filter(s => seriesVisibility[s] !== false);
        
        // 绘制每个序列的柱子
        visibleSeries.forEach(series => {
          const yValue = seriesMap.get(series);
          if (yValue === undefined) return;
          
          const color = getSeriesColor(series);
          
          // 计算柱子位置
          const xPos = xScale(String(xValue))! + xSubScale(series)!;
          const barWidth = xSubScale.bandwidth();
          
          // 绘制柱子
          svg.append("rect")
            .attr("x", xPos)
            .attr("y", yScale(yValue))
            .attr("width", barWidth)
            .attr("height", height - yScale(yValue))
            .attr("fill", color)
            .on('mouseover', function(event: MouseEvent) {
              const chartRect = chartRef.current!.getBoundingClientRect();
              const mouseX = event.clientX - chartRect.left;
              const mouseY = event.clientY - chartRect.top;
              
              tooltip
                .style('display', 'block')
                .style('opacity', '1')
                .style('visibility', 'visible')
                .html(`
                  <strong>${series}</strong><br/>
                  分组: ${xValue}<br/>
                  值: ${yValue.toFixed(2)}
                `)
                .style('left', `${mouseX + 10}px`)
                .style('top', `${mouseY - 30}px`);
            })
            .on('mousemove', function(event: MouseEvent) {
              const chartRect = chartRef.current!.getBoundingClientRect();
              const mouseX = event.clientX - chartRect.left;
              const mouseY = event.clientY - chartRect.top;
              
              tooltip
                .style('left', `${mouseX + 10}px`)
                .style('top', `${mouseY - 30}px`);
            })
            .on('mouseout', function() {
              tooltip
                .style('display', 'none')
                .style('opacity', '0')
                .style('visibility', 'hidden');
            });
        });
      });
    }

  }, [data, seriesList, seriesInfo, seriesVisibility, xAxisLabel, yAxisLabel, xLogScale, yLogScale, xMinValue, xMaxValue, yMinValue, yMaxValue, onSeriesClick, onVisibilityToggle]);

  // 辅助函数：创建坐标轴和图例
  const createAxesAndLegend = (svg: d3.Selection<SVGGElement, unknown, null, undefined>, width: number, height: number, margin: {top: number, right: number, bottom: number, left: number}) => {
    // 设置 x 轴比例尺 - 使用 band scale 处理分类数据
    const xScale = d3.scaleBand()
      .domain(['无数据'])
      .range([0, width])
      .padding(0.2);
    
    // 设置 y 轴比例尺
    const yScale = yLogScale
      ? d3.scaleLog().domain([Math.max(0.001, yMinValue), yMaxValue])
      : d3.scaleLinear().domain([yMinValue, yMaxValue]);
    yScale.range([height, 0]);
    
    // 添加 X 轴
    const xAxis = d3.axisBottom(xScale);
    
    // 绘制x轴
    const xAxisGroup = svg.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(xAxis);

    // 旋转x轴刻度标签
    xAxisGroup.selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .attr("transform", "rotate(-45)");

    // 计算x轴标签的合适位置
    // 获取x轴高度 (包括刻度和旋转后的文本)
    const xAxisHeight = xAxisGroup.node()?.getBBox().height || 30;
    const xLabelDistance = xAxisHeight + 10; // 在x轴高度基础上增加10px的间距
    
    // 添加X轴标签
    // 计算标签宽度 - 根据内容估算
    const estimatedLabelWidth = xAxisLabel.length * 7; // 每个字符约7px
    const labelWidth = Math.max(150, Math.min(width, estimatedLabelWidth));
    
    // 添加X轴标签
    const xLabelContainer = d3.select(chartRef.current)
      .select("svg")
      .append("g")
      .attr("transform", `translate(${margin.left + width/2}, ${margin.top + height + xLabelDistance})`);
    
    // 支持HTML标签（包括上下标）
    xLabelContainer.append("foreignObject")
      .attr("width", labelWidth)
      .attr("height", 40) // 增加高度以容纳可能的换行
      .attr("x", -labelWidth/2)  // 居中
      .attr("y", 0)
      .attr("class", "x-axis-label") // 添加类名便于识别
      .append("xhtml:div")
      .style("text-align", "center")
      .style("font-size", "12px")
      .style("overflow-wrap", "break-word") // 允许长文本换行
      .html(xAxisLabel);
    
    // 添加Y轴
    svg.append("g")
      .call(d3.axisLeft(yScale));
    
    // 添加Y轴标签
    const yLabelContainer = d3.select(chartRef.current)
      .select("svg")
      .append("g")
      .attr("transform", `translate(${margin.left - 40}, ${margin.top + height/2}) rotate(-90)`);
    
    // 支持HTML标签（包括上下标）
    yLabelContainer.append("foreignObject")
      .attr("width", 200)
      .attr("height", 30)
      .attr("x", -100)  // 居中
      .attr("y", -15)
      .append("xhtml:div")
      .style("text-align", "center")
      .style("font-size", "12px")
      .html(yAxisLabel);
    
    // 添加图例
    const legend = d3.select(chartRef.current)
      .select("svg")
      .append("g")
      .attr("transform", `translate(${margin.left + width + 30}, ${margin.top})`);
    
    // 辅助函数：获取系列的颜色
    const getSeriesColor = (series: string) => {
      return seriesInfo[series]?.color || "#000000";
    };
    
    // 记录累计高度，以便垂直排列图例项
    let cumulativeHeight = 0;
    
    seriesList.forEach((series) => {
      const color = getSeriesColor(series);
      const itemHeight = 68; // 增加高度以容纳统计信息
      const isVisible = seriesVisibility[series] !== false;
      
      // 图例项容器
      const legendItem = legend.append("g")
        .attr("class", "legend-item")
        .attr("data-series", series)
        .attr("transform", `translate(0, ${cumulativeHeight})`)
        .style("cursor", "pointer")
        .on("click", () => {
          if (onSeriesClick) {
            onSeriesClick(series);
          }
        });
      
      // 添加可见性切换图标
      legendItem.append("text")
        .attr("class", "visibility-toggle-icon")
        .attr("x", -15)
        .attr("y", 14)
        .style("cursor", "pointer")
        .style("font-size", "14px")
        .text(isVisible ? '○' : '●')
        .on('click', function(event) {
          event.stopPropagation();
          if (onVisibilityToggle) {
            onVisibilityToggle(series);
          }
        });
      
      // 绘制图例标记 - 对于柱状图，使用小矩形
      legendItem.append("rect")
        .attr("x", 0)
        .attr("y", 5)
        .attr("width", 10)
        .attr("height", 10)
        .style("fill", color);
      
      // 图例文本 - 系列名称
      legendItem.append("text")
        .attr("x", 15)
        .attr("y", 14)
        .attr("text-anchor", "start")
        .style("font-weight", "bold")
        .style("font-size", "12px")
        .style("fill", "#333")
        .html(seriesInfo[series]?.name || series);
      
      // 添加统计信息占位符
      legendItem.append("text")
        .attr("x", 15)
        .attr("y", 32)
        .attr("text-anchor", "start")
        .style("font-size", "10px")
        .style("fill", "#666")
        .html(`和: -`);
      
      legendItem.append("text")
        .attr("x", 15)
        .attr("y", 44)
        .attr("text-anchor", "start")
        .style("font-size", "10px")
        .style("fill", "#666")
        .html(`最大值: -`);
      
      legendItem.append("text")
        .attr("x", 15)
        .attr("y", 56)
        .attr("text-anchor", "start")
        .style("font-size", "10px")
        .style("fill", "#666")
        .html(`最小值: -`);
      
      legendItem.append("text")
        .attr("x", 15)
        .attr("y", 68)
        .attr("text-anchor", "start")
        .style("font-size", "10px")
        .style("fill", "#666")
        .html(`平均值: -`);
      
      // 更新累计高度
      cumulativeHeight += itemHeight + 10; // 增加间距
    });
  };

  return <div ref={chartRef} style={{ width: '100%', height: '100%' }}></div>;
};

export default BarChart;